/**
 * layer-list-ui-helpers.js
 * Pure DOM builder functions — no ArcGIS, no Vue, no callbacks.
 * Every function receives what it needs as arguments and returns a DOM element.
 */

// ─── Buttons ──────────────────────────────────────────────────────────────────

export function createSummaryButton(text, onClick) {
    const btn = document.createElement("div");
    btn.className = "btn-summary";
    btn.innerText = text;
    btn.style.padding = "8px 15px";
    btn.style.background = "#4caf50";
    btn.style.color = "white";
    btn.style.border = "none";
    btn.style.borderRadius = "4px";
    btn.style.cursor = "pointer";
    btn.style.textAlign = "center";
    btn.addEventListener("click", onClick);
    return btn;
  }
  
  export function createResetButton(onClick) {
    const btn = document.createElement("button");
    btn.className = "resetButton";
    btn.innerText = "Reset";
    btn.style.border = "1px solid #ccc";
    btn.style.borderRadius = "4px";
    btn.style.padding = "5px 10px";
    btn.style.cursor = "pointer";
    btn.style.backgroundColor = "#f9f9f9";
    btn.style.display = "inline-block";
    btn.addEventListener("click", onClick);
    return btn;
  }
  
  export function createStyledButton(text, bgColor, onClick) {
    const btn = document.createElement("button");
    btn.textContent = text;
    btn.style.padding = "8px 15px";
    btn.style.background = bgColor;
    btn.style.color = "white";
    btn.style.border = "none";
    btn.style.borderRadius = "4px";
    btn.style.cursor = "pointer";
    btn.style.flex = "1";
    btn.addEventListener("click", onClick);
    return btn;
  }
  
  // ─── Layout containers ────────────────────────────────────────────────────────
  
  /**
   * Bordered section with a bold title — used for every filter subsection.
   */
  export function createFilterSection(titleText) {
    const section = document.createElement("div");
    section.style.marginBottom = "15px";
    section.style.padding = "10px";
    section.style.border = "1px solid #ddd";
    section.style.borderRadius = "4px";
  
    const title = document.createElement("div");
    title.textContent = titleText;
    title.style.fontWeight = "bold";
    title.style.marginBottom = "10px";
    section.appendChild(title);
  
    return section;
  }
  
  export function createButtonRow(...buttons) {
    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.gap = "10px";
    row.style.marginBottom = "15px";
    buttons.forEach((btn) => row.appendChild(btn));
    return row;
  }
  
  // ─── Form elements ────────────────────────────────────────────────────────────
  
  export function createLabeledDateInput(labelText, onChange) {
    const wrapper = document.createElement("div");
  
    const label = document.createElement("label");
    label.textContent = labelText;
    label.style.display = "block";
    label.style.marginBottom = "5px";
    wrapper.appendChild(label);
  
    const input = document.createElement("input");
    input.type = "date";
    input.style.width = "100%";
    input.style.padding = "5px";
    input.style.marginBottom = "10px";
    input.addEventListener("change", (e) => onChange(e.target.value));
    wrapper.appendChild(input);
  
    return { wrapper, input };
  }
  
  export function createLabeledTextInput(labelText, placeholder, onChange) {
    const wrapper = document.createElement("div");
  
    const label = document.createElement("label");
    label.textContent = labelText;
    label.style.display = "block";
    label.style.marginBottom = "5px";
    wrapper.appendChild(label);
  
    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = placeholder;
    input.style.width = "100%";
    input.style.padding = "5px";
    input.style.marginBottom = "10px";
    input.style.border = "1px solid #ccc";
    input.style.borderRadius = "4px";
    input.addEventListener("input", (e) => onChange(e.target.value));
    wrapper.appendChild(input);
  
    return { wrapper, input };
  }
  
  // ─── Checkbox / Radio groups ──────────────────────────────────────────────────
  
  /**
   * Creates a group of checkboxes.
   * @param {string[]} labels
   * @param {string} groupName  - name attribute shared by all checkboxes
   * @param {function} onChange - called with the full array of currently checked values
   * @param {boolean} allChecked - initial checked state
   * @returns HTMLElement
   */
  export function createCheckboxGroup(labels, groupName, onChange, allChecked = false) {
    const container = document.createElement("div");
    container.style.marginTop = "10px";
  
    labels.forEach((label, index) => {
      const wrapper = document.createElement("div");
      wrapper.className = "radio-option";
  
      const checkbox = document.createElement("input");
      checkbox.className = "radioButton";
      checkbox.type = "checkbox";
      checkbox.name = groupName;
      checkbox.value = label;
      checkbox.checked = allChecked;
      checkbox.addEventListener("change", () => {
        const checked = [...document.getElementsByName(groupName)]
          .filter((cb) => cb.checked)
          .map((cb) => cb.value);
        onChange(checked);
      });
  
      const lbl = document.createElement("label");
      lbl.htmlFor = `${groupName}-${index}`;
      lbl.textContent = label;
      lbl.className = "radio-label";
  
      wrapper.appendChild(checkbox);
      wrapper.appendChild(lbl);
      container.appendChild(wrapper);
    });
  
    return container;
  }
  
  /**
   * Creates a group of radio buttons.
   * @param {string[]} labels
   * @param {string} groupName
   * @param {function} onChange - called with the selected index
   * @returns HTMLElement
   */
  export function createRadioGroup(labels, groupName, onChange) {
    const container = document.createElement("div");
  
    labels.forEach((label, index) => {
      const wrapper = document.createElement("div");
      wrapper.className = "radio-option";
  
      const radio = document.createElement("input");
      radio.className = "radioButton";
      radio.type = "radio";
      radio.name = groupName;
      radio.id = `${groupName}-radio-${index}`;
      radio.addEventListener("change", () => onChange(index));
  
      const lbl = document.createElement("label");
      lbl.htmlFor = `${groupName}-radio-${index}`;
      lbl.textContent = label;
      lbl.className = "radio-label";
  
      wrapper.appendChild(radio);
      wrapper.appendChild(lbl);
      container.appendChild(wrapper);
    });
  
    return container;
  }
  
  // ─── Toggle switch (gt / lt) ──────────────────────────────────────────────────
  
  export function createToggleSwitch(onGt, onLt) {
    const switchLabel = document.createElement("label");
    switchLabel.className = "switch";
  
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = true;
  
    const span = document.createElement("span");
    span.className = "slider round";
  
    switchLabel.appendChild(input);
    switchLabel.appendChild(span);
  
    input.addEventListener("change", (e) => {
      if (e.target.checked) {
        onGt();
      } else {
        onLt();
      }
    });
  
    return switchLabel;
  }
  
  // ─── Color swatch helpers ─────────────────────────────────────────────────────
  
  /**
   * Round color swatch used in legend items.
   * @param {string} color - CSS color string OR rgba array [r,g,b,a]
   */
  export function createColorSwatch(color, size = 15) {
    const swatch = document.createElement("div");
    swatch.className = "color-swatch";
    swatch.style.width = `${size}px`;
    swatch.style.height = `${size}px`;
    swatch.style.display = "inline-block";
    swatch.style.marginRight = "5px";
    swatch.style.borderRadius = "50%";
    swatch.style.flexShrink = "0";
    swatch.style.alignSelf = "center";
  
    if (Array.isArray(color)) {
      const [r, g, b, a] = color;
      swatch.style.backgroundColor = `rgba(${r},${g},${b},${a / 255})`;
    } else {
      swatch.style.backgroundColor = color;
    }
  
    return swatch;
  }
  
  /**
   * Raster legend row: swatch + label text, no checkbox.
   */
  export function createRasterLegendItem(range) {
    const wrapper = document.createElement("div");
    wrapper.className = "checkbox-option";
  
    const swatch = createColorSwatch(range.color);
    swatch.style.borderRadius = "50%";
    swatch.style.clipPath = "none";
  
    const label = document.createElement("label");
    label.innerText = range.label;
    label.className = "checkbox-label";
  
    wrapper.appendChild(swatch);
    wrapper.appendChild(label);
    return wrapper;
  }
  
  /**
   * Maize production polygon legend swatch: irregular polygon shape.
   */
  export function createPolygonLegendItem(color, labelText, onChange) {
    const item = document.createElement("div");
    item.className = "legend-item";
  
    const swatch = document.createElement("span");
    swatch.className = "color-swatch";
    swatch.style.backgroundColor = color;
    swatch.style.width = "20px";
    swatch.style.height = "20px";
    swatch.style.marginRight = "8px";
    swatch.style.display = "inline-block";
    swatch.style.clipPath = "polygon(10% 10%, 90% 5%, 85% 90%, 5% 85%)";
    swatch.style.border = "1px solid #ccc";
    item.appendChild(swatch);
  
    const rangeLabel = document.createElement("span");
    rangeLabel.className = "range-label";
    rangeLabel.textContent = labelText;
    rangeLabel.style.flexGrow = "1";
    rangeLabel.style.marginRight = "8px";
    item.appendChild(rangeLabel);
  
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = true;
    checkbox.style.accentColor = "green";
    checkbox.addEventListener("change", () => onChange(checkbox.checked));
    item.appendChild(checkbox);
  
    return { item, checkbox };
  }
  
  // ─── Dropdown (Feedstock / Offtake group layer selector) ─────────────────────
  
  /**
   * Pure dropdown builder. layerSelectionState is passed in so this function
   * has no hidden closure dependencies.
   */
  export function createGroupLayerDropdown(groupLayer, layerTitles, layerSelectionState) {
    const container = document.createElement("div");
    container.className = "layer-dropdown-container";
    container.style.padding = "10px 0";
    container.style.background = "transparent";
    container.style.border = "none";
    container.style.display =
      groupLayer.visible && groupLayer.parent.visible ? "block" : "none";
  
    const stateKey = groupLayer.title.includes("Feedstock") ? "FeedStock" : "OffTake";
    const selectedLayers = layerSelectionState[stateKey];
  
    const titleEl = document.createElement("div");
    titleEl.textContent = groupLayer.title.includes("Feedstock")
      ? "Select Feedstock"
      : "Select type of OffTake layer to be shown";
    titleEl.style.fontWeight = "bold";
    titleEl.style.marginBottom = "5px";
    titleEl.style.marginLeft = "10px";
    container.appendChild(titleEl);
  
    const checkboxContainer = document.createElement("div");
    checkboxContainer.style.maxHeight = "200px";
    checkboxContainer.style.overflowY = "auto";
    checkboxContainer.style.margin = "5px 10px";
    checkboxContainer.style.padding = "5px";
    checkboxContainer.style.border = "1px solid #ccc";
    checkboxContainer.style.borderRadius = "4px";
  
    // "All" checkbox
    const allWrapper = document.createElement("div");
    allWrapper.style.padding = "3px 0";
    const allCheckbox = document.createElement("input");
    allCheckbox.type = "checkbox";
    allCheckbox.id = `all-checkbox-${stateKey}`;
    allCheckbox.checked = selectedLayers.has("all");
    const allLabel = document.createElement("label");
    allLabel.htmlFor = `all-checkbox-${stateKey}`;
    allLabel.textContent = "All";
    allLabel.style.marginLeft = "5px";
    allWrapper.appendChild(allCheckbox);
    allWrapper.appendChild(allLabel);
    checkboxContainer.appendChild(allWrapper);
  
    const feedstockTitleMap = {
      "Maize Production 2023": "Maize",
      "Niger Delta Oil Palm": "PKS",
      "Processing Factories": "Factory Waste",
    };
  
    const layersToDisplay =
      stateKey === "FeedStock" && layerTitles
        ? layerTitles.map((t) => ({
            title: t,
            layer: groupLayer.layers.find((l) => l.title === t),
          }))
        : groupLayer.layers.map((l) => ({ title: l.title, layer: l }));
  
    const layerCheckboxes = [];
    layersToDisplay.forEach(({ title, layer }, index) => {
      if (!layer && stateKey === "FeedStock") {
        console.warn(`Layer not found for title: ${title}`);
        return;
      }
  
      const itemWrapper = document.createElement("div");
      itemWrapper.style.padding = "3px 0";
  
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.id = `layer-checkbox-${stateKey}-${index}`;
      cb.value = title;
      cb.checked = selectedLayers.has("all") || selectedLayers.has(title);
      layerCheckboxes.push(cb);
  
      const lbl = document.createElement("label");
      lbl.htmlFor = `layer-checkbox-${stateKey}-${index}`;
      lbl.textContent =
        stateKey === "FeedStock" && feedstockTitleMap[title]
          ? feedstockTitleMap[title]
          : title;
      lbl.style.marginLeft = "5px";
  
      itemWrapper.appendChild(cb);
      itemWrapper.appendChild(lbl);
      checkboxContainer.appendChild(itemWrapper);
    });
  
    function updateLayerVisibility() {
      const showAll = selectedLayers.has("all");
      groupLayer.layers.forEach((layer) => {
        layer.visible = showAll || selectedLayers.has(layer.title);
      });
    }
  
    function initializeLayerVisibility() {
      if (selectedLayers.has("all")) {
        groupLayer.layers.forEach((layer) => {
          selectedLayers.add(layer.title);
          layer.visible = true;
        });
      } else {
        groupLayer.layers.forEach((layer) => {
          layer.visible = selectedLayers.has(layer.title);
        });
      }
      updateLayerVisibility();
    }
  
    allCheckbox.addEventListener("change", (e) => {
      const isChecked = e.target.checked;
      layerCheckboxes.forEach((cb) => (cb.checked = isChecked));
      selectedLayers.clear();
      if (isChecked) {
        selectedLayers.add("all");
        layersToDisplay.forEach(({ title }) => selectedLayers.add(title));
      }
      updateLayerVisibility();
    });
  
    layerCheckboxes.forEach((cb) => {
      cb.addEventListener("change", (e) => {
        const isChecked = e.target.checked;
        const layerTitle = e.target.value;
        if (isChecked) {
          selectedLayers.add(layerTitle);
          if (layerCheckboxes.every((c) => c.checked)) {
            allCheckbox.checked = true;
            selectedLayers.add("all");
          }
        } else {
          selectedLayers.delete(layerTitle);
          allCheckbox.checked = false;
          selectedLayers.delete("all");
        }
        updateLayerVisibility();
      });
    });
  
    container.appendChild(checkboxContainer);
  
    // Reset button
    const resetBtn = document.createElement("button");
    resetBtn.textContent = "Reset";
    resetBtn.style.marginLeft = "10px";
    resetBtn.style.border = "1px solid #ccc";
    resetBtn.style.borderRadius = "4px";
    resetBtn.style.padding = "5px 10px";
    resetBtn.style.cursor = "pointer";
    resetBtn.style.backgroundColor = "#f9f9f9";
    resetBtn.addEventListener("click", () => {
      allCheckbox.checked = true;
      layerCheckboxes.forEach((cb) => (cb.checked = true));
      selectedLayers.clear();
      selectedLayers.add("all");
      layersToDisplay.forEach(({ title }) => selectedLayers.add(title));
      updateLayerVisibility();
    });
  
    const btnWrapper = document.createElement("div");
    btnWrapper.style.margin = "5px 10px";
    btnWrapper.appendChild(resetBtn);
    container.appendChild(btnWrapper);
  
    // Watchers
    groupLayer.watch("visible", (isVisible) => {
      container.style.display =
        isVisible && groupLayer.parent.visible ? "block" : "none";
      if (isVisible) updateLayerVisibility();
    });
  
    groupLayer.parent.watch("visible", (isVisible) => {
      container.style.display =
        isVisible && groupLayer.visible ? "block" : "none";
      if (isVisible && groupLayer.visible) updateLayerVisibility();
    });
  
    initializeLayerVisibility();
    return container;
  }